import { useEffect, useState } from "react";

/**
 * Whether the phone is currently reaching the server.
 *
 * The service worker will happily serve last week's schedule from cache, which
 * is the right behaviour — but the same principle the kitchen screen is built
 * on applies here. Showing old data is fine; showing old data while looking
 * current is not.
 *
 * `navigator.onLine` alone is not enough: it reports whether there is *a*
 * network, not whether Frankie answered. So a failed request marks the app
 * offline and a successful one clears it.
 */

let offline = false;
const listeners = new Set<(value: boolean) => void>();

function set(value: boolean): void {
  if (offline === value) return;
  offline = value;
  for (const listener of listeners) listener(value);
}

/** Called by the API client on every request. */
export function noteRequest(succeeded: boolean): void {
  set(!succeeded);
}

export function useOffline(): boolean {
  const [value, setValue] = useState(offline);

  useEffect(() => {
    listeners.add(setValue);

    const onOnline = (): void => set(false);
    const onOffline = (): void => set(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      listeners.delete(setValue);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  return value;
}
